import Vue from "vue";
import i18n from "@/plugins/i18n/translations";

export default {
	state: {
		requirements: [],
		actualRequirement: {},
		requirementDetails: {},
		actualRequirementWithInactivatedQuestions: {},
		specificRequirements: [],
		generalRequirements: [],
		familyRequirement: [],
		companyRequirement: [],
		stepActions: [],
		specificRequirementPagination: {},
		allRequirmentQuestionList: [],
		requirementInviteList: [],
		requirementEditConditionProps: null,
		requirementQuestionDetails: []
	},
	mutations: {
		setRequirementsOnState(state, requirements) {
			state.requirements = mapRequirementsFromBack(requirements);
		},
		setRequirementOnState(state, actualRequirement) {
			state.actualRequirement = mapRequirementFromBack(actualRequirement);
		},
		setRequirementDetailsOnState(state, actualRequirement) {
			state.requirementDetails = mapRequirementDetailsFromBack(actualRequirement);
		},
		setRequirementWithInactivatedQuestions(state, actualRequirementWithInactivatedQuestions) {
			state.actualRequirementWithInactivatedQuestions = mapRequirementFromBack(
				actualRequirementWithInactivatedQuestions
			);
		},
		setQuestionOnState(state, question) {
			const questionIndex = state.requirementDetails.questions.findIndex((q) => q.idQuestion === question.idQuestion);

			if (questionIndex === -1) {
				console.warn(`Questão com ID ${question.idQuestion} não encontrada no estado`);
				return;
			}

			updateQuestionOnState(state.requirementDetails.questions[questionIndex], question);

			const stateQuestion = structuredClone(state.requirementDetails.questions[questionIndex]);

			updateRequirementQuestionDetails(state, stateQuestion);
		},
		setQuestionAlternativeConditionsOnState(state, data) {
			let questionIndex = state.requirementDetails.questions.findIndex((q) => q.idQuestion == data.idQuestion);
			let stateQuestion = state.requirementDetails.questions[questionIndex];
			setAlternativeConditionsOnQuestion(stateQuestion, data.idAlternative, data.conditionGroup);
		},
		setGeneralRequirementsOnState(state, requirements) {
			state.generalRequirements = requirements.filter(
				(req) => req.requirementType == Vue.prototype.$requirementType.GENERAL
			);
		},
		cleanGeneralRequirements(state) {
			state.generalRequirements = [];
		},
		setFamilyRequirementOnState(state, requirements) {
			state.familyRequirement = requirements.filter(
				(req) => req.requirementType == Vue.prototype.$requirementType.DEFINITION_OF_FAMILIES
			);
		},
		setCompanyRequirementOnState(state, requirements) {
			state.companyRequirement = requirements.filter(
				(req) => req.requirementType == Vue.prototype.$requirementType.DEFINITION_OF_COMPANIES
			);
		},
		setStepActionsOnState(state, stepActions) {
			state.stepActions = stepActions;
		},
		setSpecificRequirementsOnState(state, specificRequirements) {
			state.specificRequirements = specificRequirements;
		},
		cleanSpecificRequirements(state) {
			state.specificRequirements = [];
		},
		cleanRequirementInviteList(state) {
			state.requirementInviteList = [];
		},
		updateListWithLoadedQuestions(state) {
			const { actualRequirement, requirementDetails } = state;
			const loadedQuestions = requirementDetails?.questions?.filter(
				(question) => question.loaded || question.loadedQuestionCondition
			);

			updateActualRequirementQuestions(loadedQuestions, actualRequirement);
			removeUnmatchedQuestions(actualRequirement, requirementDetails);
		},
		setPaginationSpecificRequirementOnState(state, specificRequirementPagination) {
			state.specificRequirementPagination = specificRequirementPagination;
		},
		setAllRequirementQuestionListOnState(state, setAllRequirementQuestionListOnState) {
			state.allRequirmentQuestionList = setAllRequirementQuestionListOnState;
		},
		cleanStepActionsOnState(state) {
			state.stepActions = [];
		},
		cleanActualRequirementOnState(state) {
			state.actualRequirement = {};
		},
		cleanRequirementDetailstOnState(state) {
			state.requirementDetails = {};
		},
		setRequirementInviteList(state, requirementList) {
			state.requirementInviteList = requirementList;
		},
		setRequirementEditScreenProps(state, props) {
			const originRoute = props.originRoute || "requirement";
			const payload = {
				...props,
				originRoute
			};
			state.requirementEditConditionProps = payload;
			sessionStorage.setItem("reqEditToConditionScreen", JSON.stringify(payload));
		},
		clearRequirementEditScreenProps(state) {
			state.requirementEditConditionProps = null;
			sessionStorage.removeItem("reqEditToConditionScreen");
		}
	},
	actions: {
		getRequirements({ commit }, filter = {}) {
			const { isIframeContext = false, ...filterParams } = filter;
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/", filterParams, { isIframeContext })
					.then((response) => {
						this.state.loading = false;
						commit("setRequirementsOnState", response.data.data.list);
						commit("setPaginationSpecificRequirementOnState", response.data.data.pagination);
						resolve(response);
					})
					.catch(() => {
						this.state.loading = false;
					});
			});
		},
		getRequirementQuestions(_, idRequirement) {
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get(`requirement/${idRequirement}/questions`)
					.then((response) => {
						let mappedResponse = mapQuestionsFromBack(response.data.data);
						resolve(mappedResponse);
					})
					.catch((error) => {
						console.error(error);
					});
			});
		},
		async getRequirement({ commit }, requirementData) {
			const { isIframeContext = false, ...requirement } = requirementData;
			const requestUri = `requirement/${requirement.idRequirement}/${
				requirement.idFamily || 0
			}/${!!requirement.includeContidions}`;
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get(requestUri, {}, { isIframeContext })
					.then((response) => {
						commit("setRequirementOnState", response.data.data);
						resolve(response);
					})
					.finally(() => {
						this.state.loading = false;
					});
			});
		},
		getRequirementDetails({ commit }, requirementData) {
			const { isIframeContext = false, ...requirement } = requirementData;
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/details/" + requirement.idRequirement, {}, { isIframeContext })
					.then((response) => {
						this.state.loading = false;
						commit("setRequirementDetailsOnState", response.data.data);
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
						this.state.loading = false;
					});
			});
		},
		getQuestionDetails({ commit }, questionData) {
			const { isIframeContext = false, ...question } = questionData;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/question/" + question.idQuestion, {}, { isIframeContext })
					.then((response) => {
						this.state.loading = false;
						commit("setQuestionOnState", response.data.data);
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
					});
			});
		},
		getPagedConditions(_, data) {
			return new Promise((resolve) => {
				this.state.loading = true;
				Vue.prototype.$http
					.post("requirement/question/conditions", data)
					.then((response) => {
						this.state.loading = false;
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
					})
					.finally(() => {
						this.state.loading = false;
					});
			});
		},
		updateConditionGroup(_, payload) {
			return new Promise((resolve, reject) => {
				this.state.loading = true;
				Vue.prototype.$http
					.put("requirement/question/conditionGroup", payload)
					.then(() => {
						resolve();
					})
					.catch((error) => {
						console.error(error);
						reject();
					})
					.finally(() => {
						this.state.loading = false;
					});
			});
		},
		createOrUpdateCondition(_, payload) {
			return new Promise((resolve, reject) => {
				this.state.loading = true;
				Vue.prototype.$http
					.post("requirement/question/condition", payload)
					.then(() => {
						resolve();
					})
					.catch((error) => {
						console.error(error);
						reject();
					})
					.finally(() => {
						this.state.loading = false;
					});
			});
		},
		deleteCondition(_, { idCondition, idConditionGroup }) {
			return new Promise((resolve, reject) => {
				Vue.prototype.$http
					.delete(`requirement/question/condition`, {
						params: {
							idCondition,
							idConditionGroup
						}
					})
					.then(() => resolve())
					.catch((error) => {
						console.error("Erro ao deletar condição:", error);
						reject(error);
					});
			});
		},
		getAlternativeConditions({ commit }, data) {
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/question/alternative/conditions/" + data.idAlternative)
					.then((response) => {
						data.conditionGroup = response.data.data.conditionGroup;
						commit("setQuestionAlternativeConditionsOnState", data);
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
					});
			});
		},
		getRequirementWithInactivatedQuestions({ commit }, payload = {}) {
			const { isIframeContext = false, ...data } = payload;
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get(
						"requirement/" + data.idRequirement + "/" + data.idInvite + "/questions/inactive",
						{},
						{ isIframeContext }
					)
					.then((response) => {
						this.state.loading = false;
						commit("setRequirementWithInactivatedQuestions", response.data.data);
						resolve(response);
					})
					.catch((error) => {
						console.error(error);
						this.state.loading = false;
					});
			});
		},
		getGeneralRequirements({ commit }, requestData) {
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/generalRequirements/", requestData)
					.then((response) => {
						commit("setFamilyRequirementOnState", response.data.data);
						commit("setGeneralRequirementsOnState", response.data.data);
						commit("setCompanyRequirementOnState", response.data.data);
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
					})
					.finally(() => {
						this.state.loading = false;
					});
			});
		},
		getRequirementListByIdInvite({ commit }, idInvite) {
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/inviteRequirementList/" + idInvite)
					.then((response) => {
						commit("setSpecificRequirementsOnState", response.data.data.specificRequirement);
						commit("setGeneralRequirementsOnState", response.data.data.generalRequirement);
						commit("setStepActionsOnState", response.data.data.actions);
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
					});
			});
		},
		getRequirementListByIdInviteV2({ commit }, params) {
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/inviteRequirementList", params)
					.then((response) => {
						commit("setRequirementInviteList", response.data.data.requirementList);
						commit("setStepActionsOnState", response.data.data.actions);
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
					});
			});
		},
		getRequirementGroups({ commit }) {
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/groups")
					.then((response) => {
						this.state.loading = false;
						commit("setRequirementGroupsOnState", response.data.data);
						resolve(response);
					})
					.catch((error) => {
						console.error(error);
						this.state.loading = false;
					});
			});
		},
		getAllRequirementQuestionListByClient({ commit }, filter = {}) {
			const { isIframeContext = false, ...filterParams } = filter;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/allRequirementQuestion", filterParams, { isIframeContext })
					.then((response) => {
						commit("setAllRequirementQuestionListOnState", response.data.data);
						resolve(response);
					})
					.catch((error) => {
						console.error(error);
					});
			});
		},
		updateSystemRequirement(_, systemRequirement) {
			this.state.loading = true;
			return new Promise((resolve) => {
				mapSystemRequirementToBack(systemRequirement).then((mappedSystemRequirement) => {
					Vue.prototype.$http.put("requirement/systemRequirement", mappedSystemRequirement).then((response) => {
						this.state.loading = false;
						resolve(response);
					});
				});
			}).catch((error) => {
				console.error(error);
				this.state.loading = false;
			});
		},
		async createRequirement(_, requirement) {
			this.state.loading = true;
			return await new Promise((resolve, reject) => {
				mapRequirementToBack(requirement)
					.then((mappedRequirement) => {
						Vue.prototype.$http
							.post("requirement", mappedRequirement)
							.then((response) => {
								this.state.loading = false;
								resolve(response.data.data.idRequirement);
							})
							.catch((error) => {
								this.state.loading = false;
								reject(error.message);
							});
					})
					.catch((err) => {
						console.error(err);
						this.state.loading = false;
					});
			});
		},
		async createRequirementSimple(_, payload) {
			const requirementData = payload.requirementData || payload;
			const config = payload.config || {};
			const { isIframeContext = false } = config;
			this.state.loading = true;
			return await new Promise((resolve, reject) => {
				mapRequirementToBack(requirementData)
					.then((mappedRequirement) => {
						Vue.prototype.$http
							.post("requirement/create-with-family", mappedRequirement, { isIframeContext })
							.then((response) => {
								this.state.loading = false;
								const { isError, data } = response.data;
								if (isError) {
									reject(data?.code);
									return;
								}
								resolve(data);
							})
							.catch((error) => {
								this.state.loading = false;
								reject(error.message);
							});
					})
					.catch((err) => {
						console.error(err);
						this.state.loading = false;
					});
			});
		},
		async editRequirement(_, payload) {
			const requirementData = payload.requirementData || payload;
			const config = payload.config || {};
			const { isIframeContext = false } = config;
			this.state.loading = true;
			return new Promise((resolve, reject) => {
				mapRequirementToBack(requirementData).then((mappedRequirement) => {
					Vue.prototype.$http
						.put("requirement", mappedRequirement, { isIframeContext })
						.then((response) => {
							this.state.loading = false;
							const { data } = response.data;
							resolve(data);
						})
						.catch((err) => {
							console.error(err);
							reject();
						});
				});
			}).catch((error) => {
				console.error(error);
				this.state.loading = false;
			});
		},
		deleteRequirement(_, payload = {}) {
			const { isIframeContext = false, ...requirement } = payload;
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.delete("requirement/" + requirement.idRequirement, {}, { isIframeContext })
					.then((response) => {
						this.state.loading = false;
						resolve(response);
					});
			}).catch((error) => {
				console.error(error);
				this.state.loading = false;
			});
		},
		requirementIsLinked(_, idRequirement) {
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http.get("requirement/" + idRequirement + "/linked").then((response) => {
					this.state.loading = false;
					resolve(response.data.data);
				});
			}).catch((error) => {
				console.error(error);
				this.state.loading = false;
			});
		},
		checkActiveInviteWithRequirement(_, idRequirement) {
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http.get("requirement/" + idRequirement + "/checkInvite").then((response) => {
					this.state.loading = false;
					resolve(response.data.data);
				});
			}).catch((error) => {
				console.error(error);
				this.state.loading = false;
			});
		},
		downloadRequirementTemplate() {
			this.state.loading = true;
			Vue.prototype.$http
				.download("requirement/template/download")
				.then((response) => {
					this.state.loading = false;

					if (response.data.error) {
						Vue.prototype.$functions.displayError(this._vm, response.data.error);
						return;
					}

					var fURL = document.createElement("a");

					fURL.href = window.URL.createObjectURL(new Blob([response.data]));
					fURL.setAttribute("download", "Template de Opções de Requisitos.xlsx");
					document.body.appendChild(fURL);

					fURL.click();
				})
				.catch((error) => {
					console.error(error);
					this.state.loading = false;
				});
		},
		uploadRequirementTemplate(_, data) {
			return new Promise((resolve, reject) => {
				let formData = new FormData();
				formData.append("file", data);
				Vue.prototype.$http
					.post("requirement/template/upload", formData)
					.then((response) => {
						resolve(response.data.data);
					})
					.catch((error) => {
						reject(error);
					});
			});
		},
		fetchErpIntegrationRequirements(_, params) {
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/integration", params)
					.then((response) => {
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
					});
			});
		},
		getSystemRequirement({ commit }, idRequirement) {
			this.state.loading = true;
			return new Promise((resolve) => {
				Vue.prototype.$http
					.get("requirement/systemRequirement", { id: idRequirement })
					.then((response) => {
						commit("setRequirementOnState", response.data.data);
						resolve(response.data.data);
					})
					.finally(() => {
						this.state.loading = false;
					});
			});
		},
		getAllRequirementActors(_, idRequirementList) {
			return new Promise((resolve) => {
				Vue.prototype.$http
					.post("requirement/requirementActor", { IdRequirementList: idRequirementList })
					.then((response) => {
						resolve(response.data.data);
					})
					.catch((error) => {
						console.error(error);
					});
			});
		}
	},
	getters: {
		allRequirements({ requirements }) {
			return requirements;
		},
		requirements({ requirements }) {
			return requirements.filter((req) => !req.isIntegration);
		},
		systemRequirements({ requirements }) {
			return requirements.filter((req) => req.isIntegration);
		},
		requirement({ actualRequirement }) {
			return actualRequirement;
		},
		requirementDetails({ requirementDetails }) {
			return requirementDetails;
		},
		requirementQuestionDetails({ requirementQuestionDetails }) {
			return requirementQuestionDetails;
		},
		requirementWithInactivatedQuestions({ actualRequirementWithInactivatedQuestions }) {
			return actualRequirementWithInactivatedQuestions;
		},
		requirementGroups({ requirementGroups }) {
			return requirementGroups;
		},
		generalRequirements({ generalRequirements }) {
			return generalRequirements;
		},
		specificRequirements({ specificRequirements }) {
			return specificRequirements;
		},
		familyRequirement({ familyRequirement }) {
			return familyRequirement;
		},
		companyRequirement({ companyRequirement }) {
			return companyRequirement;
		},
		stepActions({ stepActions }) {
			return stepActions;
		},
		specificRequirementPagination({ specificRequirementPagination }) {
			return specificRequirementPagination;
		},
		allRequirmentQuestionList({ allRequirmentQuestionList }) {
			return allRequirmentQuestionList;
		},
		requirementInviteList({ requirementInviteList }) {
			return requirementInviteList;
		},
		getRequirementEditScreenProps(state) {
			const { requirementEditConditionProps } = state;
			if (requirementEditConditionProps) {
				return requirementEditConditionProps;
			}

			const stored = sessionStorage.getItem("reqEditToConditionScreen");
			if (stored) {
				var propsStored = JSON.parse(stored);
				state.requirementEditConditionProps = propsStored;
				return propsStored;
			}

			return null;
		}
	}
};

const mapSystemRequirementToBack = (systemRequirement) => {
	return new Promise((resolve) => {
		let obj = {
			IdRequirement: systemRequirement.idRequirement,
			RequirementType: systemRequirement.requirementType,
			IdValidateActor: systemRequirement.idValidateActor,
			RequirementActorsList: systemRequirement.requirementActorsList,
			ConditionGroup: systemRequirement.conditionGroup,
			IdQuestion: systemRequirement.idQuestion
		};
		resolve(obj);
	});
};

const mapRequirementToBack = (requirement) => {
	return new Promise((resolve) => {
		let questions = [];
		let requirementActors = [];
		let requirementPersonTypes = [];
		let ordenationQuestionValidation = 0;
		let ordenationQuestionFiller = 0;
		if (requirement.requirementActorsList) {
			requirement.requirementActorsList.forEach((actor) => {
				requirementActors.push({
					idActor: actor.idActor,
					role: actor.role
				});
			});
		}

		if (requirement.requirementPersonTypeList) {
			requirement.requirementPersonTypeList.forEach((requirementPersonType) => {
				requirementPersonTypes.push(requirementPersonType.idPersonType);
			});
		}
		requirement.questions.forEach((question) => {
			let alternatives = [];
			let ordenationAlternative = 0;
			if (question.responseType == Vue.prototype.$ResponseType.MultipleSelection) {
				question.checkAlternatives.forEach((alternative) => {
					alternatives.push({
						IdAlternative: alternative.idAlternative > 0 ? alternative.idAlternative : 0,
						Description: alternative.description,
						CodeERP: alternative.codeERP,
						IsDefault: alternative.isDefault,
						Updated: alternative.loadedAlternativeCondition ? true : false,
						ConditionGroup: mapConditionGroupToBack(alternative.conditionGroup),
						Ordenation: ordenationAlternative,
						Score: parseFloat(alternative.score)
					});
					ordenationAlternative++;
				});
			} else if (question.responseType == Vue.prototype.$ResponseType.UniqueSelection) {
				question.radioAlternatives.forEach((alternative) => {
					alternatives.push({
						IdAlternative: alternative.idAlternative,
						Description: alternative.description,
						CodeERP: alternative.codeERP,
						IsDefault: alternative.isDefault,
						Updated: alternative.loadedAlternativeCondition ? true : false,
						ConditionGroup: mapConditionGroupToBack(alternative.conditionGroup),
						Ordenation: ordenationAlternative,
						Score: parseFloat(alternative.score)
					});
					ordenationAlternative++;
				});
			} else if (question.responseType == Vue.prototype.$ResponseType.Dropdown) {
				question.dropdownAlternatives.forEach((alternative) => {
					alternatives.push({
						IdAlternative: alternative.idAlternative,
						Description: alternative.description,
						CodeERP: alternative.codeERP,
						IsDefault: alternative.isDefault,
						Updated: alternative.loadedAlternativeCondition ? true : false,
						ConditionGroup: mapConditionGroupToBack(alternative.conditionGroup),
						Ordenation: ordenationAlternative,
						Score: parseFloat(alternative.score)
					});
					ordenationAlternative++;
				});
			} else if (question.responseType == Vue.prototype.$ResponseType.Concatenation) {
				question.concatenationAlternatives.forEach((alternative) => {
					alternatives.push({
						IdAlternative: alternative.idAlternative,
						Description: alternative.description,
						CodeERP: alternative.codeERP,
						IsDefault: alternative.isDefault,
						IsNewAlternative: alternative.isNewAlternative ? true : false,
						ConcatenationGroup: mapConcatenationGroupToBack(alternative.concatenationGroup),
						Ordenation: ordenationAlternative
					});
					ordenationAlternative++;
				});
			}
			questions.push({
				IdQuestion: question.idQuestion > 0 ? question.idQuestion : 0,
				Title: question.title,
				CodeERP: question.codeERP,
				Identifier: question.identifier ?? 0,
				IsMandatory: question.isMandatory || false,
				HasJustification: question.hasJustification || false,
				IsJustificationMandatory: question.isJustificationMandatory || false,
				ResponseType: question.responseType,
				IdStandardListType: question.standardListTypeProps?.idStandardListType,
				IdRequirementStandardList: question.standardListTypeProps?.idRequirementStandardList,
				Link: question.linkedTo,
				Alternatives: alternatives,
				ConditionGroup: mapConditionGroupToBack(question.conditionGroup),
				Updated: question.loaded || question.loadedQuestionCondition ? true : false,
				ConditionGroupUpdated: question.loadedQuestionCondition ? true : false,
				IsActive: question.isActive || false,
				Ordenation: requirement.idRequirement
					? question.ordenationQuestion
					: question.formType == Vue.prototype.$FormType.FillerForm
					? ordenationQuestionFiller++
					: ordenationQuestionValidation++,
				MinLength: parseInt(question.minLength) || 0,
				MaxLength: parseInt(question.maxLength) || 4000,
				FormType: question.formType,
				IsRefQuestionToStandardList: question.isRefQuestionToStandardList
			});
		});

		let conditionScores = [];
		conditionScores = requirement.conditionData.map((condition) => ({
			Id: condition.id || 0,
			IdRequirement: requirement.idRequirement,
			Type: parseInt(condition.type, 10),
			Operator: parseInt(condition.operator, 10),
			Value: parseFloat(Number(condition.value))
		}));

		let retObject = {
			IdRequirement: requirement.idRequirement,
			RequirementType: requirement.requirementType,
			DisplayType: requirement.displayType,
			Identifier: requirement.hasIdentifier
				? {
						Name: requirement.identifierName,
						Mask: requirement.identifierMask
				  }
				: null,
			IdRequirementGroup: requirement.idRequirementGroup,
			IdFile: requirement.fileId ? requirement.fileId : null,
			ValidityBeginning: Vue.prototype.$functions.formatDateToBack(requirement.validityBeginning),
			ValidityEnd: Vue.prototype.$functions.formatDateToBack(requirement.validityEnd),
			IsValidityEndIndeterminate: requirement.isValidityEndIndeterminate,
			IdFillerActor: requirement.idFillerActor,
			IdValidateActor: requirement.idValidateActor,
			HasAttachment: requirement.hasAttachment,
			IsAttachmentMandatory: requirement.isAttachmentMandatory,
			HasValidity: requirement.hasValidity,
			UserConfirm: requirement.userConfirm,
			Description: requirement.description,
			Explanation: requirement.explanation,
			HasIssueDate: requirement.hasIssueDate,
			CodeERP: requirement.codeERP,
			ValidityType: requirement.validityType,
			ValidityPeriod: requirement.validityPeriod,
			AllowAntecipateValidity: requirement.allowAntecipateValidity,
			BlockTableAnswerDeleting: requirement.blockTableAnswerDeleting,
			UseSugestionResponseValues: requirement.useSugestionResponseValues,
			ValidityDate: requirement.validityDate,
			EmissionDate: requirement.emissionDate,
			Questions: questions,
			RequirementActorsList: requirementActors,
			RequirementPersonTypeList: requirementPersonTypes,
			NewRequirementType: requirement.newRequirementType,
			ConditionScores: conditionScores,
			UseAsStandardList: requirement.useAsStandardList,
			AlwaysHomologate: requirement.alwaysHomologate
		};

		resolve(retObject);
	});
};

const mapConditionGroupToBack = (conditionGroup) => {
	if (!conditionGroup) return null;

	let conditions = [];
	(conditionGroup.conditions || []).forEach((condition) => {
		conditions.push({
			IdCondition: condition.idCondition,
			Type: condition.type,
			IdQuestionOrIntegrationField: condition.idQuestionOrIntegrationField,
			IsIntegration: condition.isIntegration,
			Operator: condition.operator,
			ValueText: condition.valueText,
			ValueId: condition.valueId,
			AnswerTypeConcatenation: condition.answerTypeConcatenation,
			Alternatives: condition.alternatives
		});
	});

	let retObj = {
		IdConditionGroup: conditionGroup.idConditionGroup,
		ConditionGroupOperator: conditionGroup.conditionGroupOperator,
		Conditions: conditions,
		IdConditionsToDelete: conditionGroup.conditionsToDelete
	};

	return retObj;
};

const mapConcatenationGroupToBack = (concatenationGroup) => {
	if (!concatenationGroup) return null;

	let concatenations = [];
	(concatenationGroup.concatenations || []).forEach((concatenation) => {
		concatenations.push({
			IdCondition: concatenation.concatenationId,
			Type: concatenation.selectedType,
			Operator: 1,
			IdQuestion: concatenation.idRequirementQuestion,
			TextValue: concatenation.textValue,
			IsNewCondition: concatenation.newConcatenation,
			AnswerTypeConcatenation: concatenation.answerTypeConcatenation
		});
	});

	let retObj = {
		IdConcatenationGroup: concatenationGroup.idConcatenationGroup,
		Concatenations: concatenations,
		IsNewConcatenationGroup: concatenationGroup.isNewConcatenationGroup
	};

	return retObj;
};

const mapRequirementsFromBack = (requirementsFromBack) => {
	let response = [];
	requirementsFromBack.forEach((requirement) => {
		response.push(mapRequirementFromBack(requirement));
	});
	return response;
};

const mapRequirementFromBack = (requirementFromBack) => {
	let questions = [];
	let familyList = [];
	let actorListView = [];
	let requirementActors = [];
	if (requirementFromBack.requirementActors) {
		requirementFromBack.requirementActors.forEach((actor) => {
			requirementActors.push({
				name: actor.name,
				idActor: actor.idActor,
				role: actor.role
			});
		});
	}

	if (requirementFromBack.questions) {
		requirementFromBack.questions.forEach((question) => {
			let alternatives = [];
			if (question.alternatives) {
				if (question.responseType == Vue.prototype.$ResponseType.Concatenation) {
					question.alternatives.forEach((alternative) => {
						alternatives.push({
							idAlternative: alternative.idAlternative,
							isDefault: alternative.isDefault,
							isActive: alternative.isActive,
							ordenationAlternative: alternative.ordenation,
							concatenationGroup: mapConcatenationsFromBack(alternative.idAlternative, alternative.conditionGroup),
							conditionGroup: mapConditionGroupFromBack(
								question.idQuestion,
								alternative.idAlternative,
								alternative.conditionGroup
							)
						});
					});
				} else {
					question.alternatives.forEach((alternative) => {
						alternatives.push({
							idAlternative: alternative.idAlternative,
							description: alternative.description,
							codeERP: alternative.codeERP,
							isDefault: alternative.isDefault,
							isActive: alternative.isActive,
							ordenationAlternative: alternative.ordenation,
							conditionGroup: mapConditionGroupFromBack(
								question.idQuestion,
								alternative.idAlternative,
								alternative.conditionGroup
							),
							loadedAlternativeCondition: false,
							score: alternative.score
						});
					});
				}
			}

			questions.push({
				idQuestion: question.idQuestion,
				title: question.title,
				codeERP: question.codeERP,
				isMandatory: question.isMandatory,
				hasJustification: question.hasJustification,
				isJustificationMandatory: question.isJustificationMandatory,
				responseType: question.responseType,
				idStandardListType: question.idStandardListType,
				linkedTo: question.linkedTo == null ? "Nenhum" : question.linkedTo,
				identifier: question.identifier,
				mask: question.mask,
				maskAllowUpper: question.maskAllowUpper,
				maskAllowLower: question.maskAllowLower,
				maskAllowNumber: question.maskAllowNumber,
				maskAllowSpecialChar: question.maskAllowSpecialChar,
				isActive: question.isActive,
				checkAlternatives: question.responseType == Vue.prototype.$ResponseType.MultipleSelection ? alternatives : [],
				radioAlternatives: question.responseType == Vue.prototype.$ResponseType.UniqueSelection ? alternatives : [],
				dropdownAlternatives: question.responseType == Vue.prototype.$ResponseType.Dropdown ? alternatives : [],
				standardListAlternatives: question.responseType == Vue.prototype.$ResponseType.StandardList ? alternatives : [],
				concatenationAlternatives:
					question.responseType == Vue.prototype.$ResponseType.Concatenation ? alternatives : [],
				visible: true,
				ordenationQuestion: question.ordenation,
				conditionGroup: mapConditionGroupFromBack(question.idQuestion, null, question.conditionGroup),
				MinLength: parseInt(question.minLength) || 0,
				MaxLength: parseInt(question.maxLength) || 4000,
				formType: question.formType,
				IsRefQuestionToStandardList: question.isRefQuestionToStandardList,
				environmentUniqueKey: question.environmentUniqueKey
			});
		});
	}
	if (requirementFromBack.familyList) {
		requirementFromBack.familyList.forEach((family) => {
			familyList.push({
				idFamily: family.idFamily,
				title: family.title,
				code: family.code,
				idParentFamily: family.idParentFamily,
				isActive: family.isActive,
				idStandard: family.idStandard,
				idClient: family.idClient
			});
		});
	}
	if (requirementFromBack.actorListViewRequirement) {
		requirementFromBack.actorListViewRequirement.forEach((actorView) => {
			actorListView.push({
				idActor: actorView.IdActor,
				idClient: actorView.IdClient,
				Name: actorView.Name,
				isActive: actorView.isActive,
				isExternal: actorView.isExternal
			});
		});
	}

	const requirementPersonTypeObj = {
		isCpf: requirementFromBack.isCpf,
		isCnpj: requirementFromBack.isCnpj,
		isNif: requirementFromBack.isNif
	};

	let response = {
		idRequirement: requirementFromBack.idRequirement,
		requirementType: requirementFromBack.requirementType ? true : false,
		displayType: requirementFromBack.displayType,
		hasIdentifier: requirementFromBack.identifier ? true : false,
		idRequirementGroup: requirementFromBack.requirementGroup
			? requirementFromBack.requirementGroup.idRequirementGroup
			: null,
		groupDescription: requirementFromBack.requirementGroup ? requirementFromBack.requirementGroup.description : null,
		fileId: requirementFromBack.idFile ? requirementFromBack.idFile : null,
		validityBeginning: requirementFromBack.validityBeginning
			? Vue.prototype.$functions.formatDateFromBack(requirementFromBack.validityBeginning)
			: null,
		validityEnd: requirementFromBack.validityEnd
			? Vue.prototype.$functions.formatDateFromBack(requirementFromBack.validityEnd)
			: null,
		isValidityEndIndeterminate: requirementFromBack.isValidityEndIndeterminate ? true : false,
		idFillerActor: requirementFromBack.idFillerActor ? requirementFromBack.idFillerActor : null,
		nameFillerActor: requirementFromBack.nameFillerActor,
		idValidateActor: requirementFromBack.idValidateActor ? requirementFromBack.idValidateActor : null,
		nameValidateActor: requirementFromBack.nameValidateActor,
		hasAttachment: requirementFromBack.hasAttachment ? true : false,
		isAttachmentMandatory: requirementFromBack.isAttachmentMandatory ? true : false,
		hasValidity: requirementFromBack.hasValidity ? true : false,
		validityType: requirementFromBack.hasValidity ? requirementFromBack.hasValidity.validityType : null,
		validityPeriod: requirementFromBack.hasValidity ? requirementFromBack.hasValidity.validityPeriod : null,
		validityData: requirementFromBack.hasValidity ? requirementFromBack.hasValidity.validityData : null,
		userConfirm: requirementFromBack.userConfirm ? requirementFromBack.userConfirm : false,
		hasJustification: false,
		mandatoryJustification: false,
		idResponseType: null,
		responseOptions: [],
		codeERP: requirementFromBack.codeERP ? requirementFromBack.codeERP : null,
		description: requirementFromBack.description ? requirementFromBack.description : null,
		explanation: requirementFromBack.explanation ? requirementFromBack.explanation : null,
		hasIssueDate: requirementFromBack.hasIssueDate ? true : false,
		emissionDate: requirementFromBack.hasIssueDate ? requirementFromBack.hasIssueDate.emissionDate : null,
		isIntegration: requirementFromBack.isIntegration ? true : false,
		idIntegration: requirementFromBack.idIntegration,
		questions: questions,
		familyListVinculed: familyList,
		workflowType: requirementFromBack.workflowTypeDescription,
		requirementActorsList: requirementActors,
		newRequirementType: requirementFromBack.newRequirementType,
		requirementPersonTypeList: mapRequirementPersonType(requirementPersonTypeObj),
		isCpf: requirementFromBack.isCpf,
		isCnpj: requirementFromBack.isCnpj,
		isNif: requirementFromBack.isNif,
		allowAntecipateValidity: requirementFromBack.allowAntecipateValidity ? true : false,
		blockTableAnswerDeleting: requirementFromBack.blockTableAnswerDeleting,
		useSugestionResponseValues: requirementFromBack.useSugestionResponseValues ? true : false,
		actorListViewRequirement: actorListView,
		useAsStandardList: requirementFromBack.useAsStandardList,
		alwaysHomologate: requirementFromBack.alwaysHomologate
	};
	return response;
};

const mapRequirementDetailsFromBack = (requirementFromBack) => {
	let questions = [];
	if (requirementFromBack.questions) {
		requirementFromBack.questions.forEach((question) => {
			questions.push({
				idQuestion: question.idQuestion,
				title: question.title,
				codeERP: question.codeERP,
				isMandatory: question.isMandatory,
				ordenationQuestion: question.ordenation,
				visible: false,
				loaded: false,
				loadedQuestionCondition: false,
				minLength: question.minLength,
				maxLength: question.maxLength,
				formType: question.formType,
				isRefQuestionToStandardList: question.isRefQuestionToStandardList
			});
		});
	}

	const requirementPersonTypeObj = {
		isCpf: requirementFromBack.isCpf,
		isCnpj: requirementFromBack.isCnpj,
		isNif: requirementFromBack.isNif
	};

	let response = {
		//Details
		idRequirement: requirementFromBack.idRequirement,
		codeERP: requirementFromBack.codeERP ? requirementFromBack.codeERP : null,
		newRequirementType: requirementFromBack.newRequirementType,
		displayType: requirementFromBack.displayType,
		useAsStandardList: requirementFromBack.useAsStandardList,
		requirementPersonTypeList: mapRequirementPersonType(requirementPersonTypeObj),
		requirementType: requirementFromBack.requirementType ? true : false,
		idRequirementGroup: requirementFromBack.requirementGroup
			? requirementFromBack.requirementGroup.idRequirementGroup
			: null,
		fileId: requirementFromBack.idFile ? requirementFromBack.idFile : null,
		hasAttachment: requirementFromBack.hasAttachment ? true : false,
		isAttachmentMandatory: requirementFromBack.isAttachmentMandatory ? true : false,
		userConfirm: requirementFromBack.userConfirm ? requirementFromBack.userConfirm : false,

		//Actors
		idFillerActor: requirementFromBack.idFillerActor ? requirementFromBack.idFillerActor : null,
		idValidateActor: requirementFromBack.idValidateActor ? requirementFromBack.idValidateActor : null,
		description: requirementFromBack.description ? requirementFromBack.description : null,
		explanation: requirementFromBack.explanation ? requirementFromBack.explanation : null,

		//Validity
		hasValidity: requirementFromBack.hasValidity ? true : false,
		validityEnd: requirementFromBack.validityEnd
			? Vue.prototype.$functions.formatDateFromBack(requirementFromBack.validityEnd)
			: null,
		validityBeginning: requirementFromBack.validityBeginning
			? Vue.prototype.$functions.formatDateFromBack(requirementFromBack.validityBeginning)
			: null,
		isValidityEndIndeterminate: requirementFromBack.isValidityEndIndeterminate ? true : false,
		validityType: requirementFromBack.hasValidity ? requirementFromBack.hasValidity.validityType : null,
		validityPeriod: requirementFromBack.hasValidity ? requirementFromBack.hasValidity.validityPeriod : null,
		validityData: requirementFromBack.hasValidity ? requirementFromBack.hasValidity.validityData : null,
		allowAntecipateValidity: requirementFromBack.allowAntecipateValidity ? true : false,
		blockTableAnswerDeleting: requirementFromBack.blockTableAnswerDeleting,
		useSugestionResponseValues: requirementFromBack.useSugestionResponseValues ? true : false,
		isReferencedAsStandardList: requirementFromBack.isReferencedAsStandardList,
		referencedRequirementTitles: requirementFromBack.referencedRequirementTitles,
		alwaysHomologate: requirementFromBack.alwaysHomologate,

		//Integration
		isIntegration: requirementFromBack.isIntegration ? true : false,
		idIntegration: requirementFromBack.idIntegration,

		//Questions
		questions: questions,
		conditionData: requirementFromBack.conditionScore ?? []
	};

	return response;
};

const mapRequirementPersonType = (requirementPersonTypeObj) => {
	let mappedRequirementPersonType = [];
	mappedRequirementPersonType.push({
		idPersonType: Vue.prototype.$RequirementPersonType.PJ,
		name: i18n.t("commons.pessoaJuridica"),
		label: requirementPersonTypeObj.isCnpj
	});
	mappedRequirementPersonType.push({
		idPersonType: Vue.prototype.$RequirementPersonType.PF,
		name: i18n.t("commons.pessoaFisica"),
		label: requirementPersonTypeObj.isCpf
	});
	mappedRequirementPersonType.push({
		idPersonType: Vue.prototype.$RequirementPersonType.NIF,
		name: i18n.t("commons.pessoaEstrangeira"),
		label: requirementPersonTypeObj.isNif
	});
	return mappedRequirementPersonType;
};

const mapConditionGroupFromBackNEW = (idQuestion, idAlternative, conditionGroupFromBack) => {
	let conditionGroup = {
		idConditionGroup: conditionGroupFromBack.idConditionGroup,
		idQuestion: idQuestion,
		idAlternative: idAlternative,
		conditionGroupOperator: conditionGroupFromBack.compositeOperator,
		conditions: conditionGroupFromBack.conditions
	};

	return conditionGroup;
};

const mapConditionGroupFromBack = (idQuestion, idAlternative, conditionGroupFromBack) => {
	if (!conditionGroupFromBack) return null;

	let conditions = [];
	if (conditionGroupFromBack.conditions) {
		conditionGroupFromBack.conditions.forEach((condition) => {
			if (condition.type != Vue.prototype.$ConditionType.CONCATENATE_IF)
				conditions.push({
					idCondition: condition.idCondition,
					type: condition.type,
					idRequirement: condition.idRequirement,
					idQuestion: condition.idQuestion,
					questionResponseType: condition.questionResponseType,
					operator: condition.operator,
					valueText: condition.valueText,
					valueId: condition.valueId,
					answerTypeConcatenation: condition.answerTypeConcatenation,
					alternatives: condition.alternatives ?? []
				});
		});
	}

	let conditionGroup = {
		idConditionGroup: conditionGroupFromBack.idConditionGroup,
		idQuestion: idQuestion,
		idAlternative: idAlternative,
		conditionGroupOperator: conditionGroupFromBack.conditionGroupOperator,
		conditions: conditions
	};

	return conditionGroup;
};

const mapConcatenationsFromBack = (idAlternative, conditionGroupFromBack) => {
	if (!conditionGroupFromBack) return null;

	let concatenations = [];
	if (conditionGroupFromBack.conditions) {
		conditionGroupFromBack.conditions.forEach((condition) => {
			concatenations.push({
				concatenationId: condition.idCondition,
				selectedType: condition.operator,
				idRequirement: condition.idRequirement,
				idRequirementQuestion: condition.idQuestion,
				textValue: condition.valueText,
				answerTypeConcatenation: condition.answerTypeConcatenation
			});
		});
	}

	return {
		idConcatenationGroup: conditionGroupFromBack.idConditionGroup,
		concatenations: concatenations
	};
};

const mapQuestionsFromBack = (questionsFromBack) => {
	let response = [];
	questionsFromBack.forEach((question) => {
		response.push(mapQuestionFromBack(question));
	});

	return response;
};

const updateQuestionOnState = (oldQuestion, question) => {
	let alternatives = [];
	if (question.alternatives) {
		if (question.responseType == Vue.prototype.$ResponseType.Concatenation) {
			question.alternatives.forEach((alternative) => {
				alternatives.push({
					idAlternative: alternative.idAlternative,
					isDefault: alternative.isDefault,
					isActive: alternative.isActive,
					ordenationAlternative: alternative.ordenation,
					concatenationGroup: mapConcatenationsFromBack(alternative.idAlternative, alternative.conditionGroup)
				});
			});
		} else {
			question.alternatives.forEach((alternative) => {
				alternatives.push({
					idAlternative: alternative.idAlternative,
					description: alternative.description,
					codeERP: alternative.codeERP,
					isDefault: alternative.isDefault,
					isActive: alternative.isActive,
					ordenationAlternative: alternative.ordenation,
					loadedAlternativeCondition: false,
					score: alternative.score
				});
			});
		}
	}

	Vue.set(oldQuestion, "isMandatory", question.isMandatory ? true : false);
	Vue.set(oldQuestion, "codeERP", question.codeERP);
	Vue.set(oldQuestion, "hasJustification", question.hasJustification);
	Vue.set(oldQuestion, "isJustificationMandatory", question.isJustificationMandatory);
	Vue.set(oldQuestion, "responseType", question.responseType);
	Vue.set(oldQuestion, "standardListTypeProps", {
		idStandardListType: question.idStandardListType,
		idRequirementStandardList: question.idRequirementStandardList,
		idStandardListTypeParent: question.idStandardListTypeParent
	});
	Vue.set(oldQuestion, "linkedTo", question.linkedTo == null ? "Nenhum" : question.linkedTo);
	Vue.set(oldQuestion, "identifier", question.idIdentifier ? question.idIdentifier : -1);
	Vue.set(oldQuestion, "mask", question.mask);
	Vue.set(oldQuestion, "isActive", question.isActive);
	Vue.set(
		oldQuestion,
		"checkAlternatives",
		question.responseType == Vue.prototype.$ResponseType.MultipleSelection ? alternatives : []
	);
	Vue.set(
		oldQuestion,
		"radioAlternatives",
		question.responseType == Vue.prototype.$ResponseType.UniqueSelection ? alternatives : []
	);
	Vue.set(
		oldQuestion,
		"dropdownAlternatives",
		question.responseType == Vue.prototype.$ResponseType.Dropdown ? alternatives : []
	);
	Vue.set(
		oldQuestion,
		"standardListAlternatives",
		question.responseType == Vue.prototype.$ResponseType.StandardList ? alternatives : []
	);
	Vue.set(
		oldQuestion,
		"concatenationAlternatives",
		question.responseType == Vue.prototype.$ResponseType.Concatenation ? alternatives : []
	);
	Vue.set(oldQuestion, "visible", true);
};

const mapQuestionFromBack = (questionFromBack) => {
	let alternativeOptions = [];

	if (questionFromBack.alternatives) {
		questionFromBack.alternatives.forEach((alternative) => {
			alternativeOptions.push({
				value: alternative.idAlternative,
				text: alternative.description
			});
		});
	}
	let response = {
		value: questionFromBack.idQuestionOrIntegrationField,
		text: questionFromBack.title,
		questionResponseType: questionFromBack.responseType,
		idStandardListType: questionFromBack.idStandardListType,
		alternativeOptions: alternativeOptions
	};

	return response;
};

const setAlternativeConditionsOnQuestion = (question, idAlternative, conditionGroup) => {
	if (conditionGroup.conditions.length == 0) return;
	let alternative = {};
	let alternativeIndex = 0;

	switch (question.responseType) {
		case Vue.prototype.$ResponseType.MultipleSelection:
			alternativeIndex = question.checkAlternatives.findIndex((alt) => alt.idAlternative == idAlternative);
			alternative = question.checkAlternatives[alternativeIndex];
			Vue.set(
				alternative,
				"conditionGroup",
				mapConditionGroupFromBackNEW(question.idQuestion, idAlternative, conditionGroup)
			);
			break;

		case Vue.prototype.$ResponseType.UniqueSelection:
			alternativeIndex = question.radioAlternatives.findIndex((alt) => alt.idAlternative == idAlternative);
			alternative = question.radioAlternatives[alternativeIndex];
			Vue.set(
				alternative,
				"conditionGroup",
				mapConditionGroupFromBackNEW(question.idQuestion, idAlternative, conditionGroup)
			);
			break;

		case Vue.prototype.$ResponseType.Dropdown:
			alternativeIndex = question.dropdownAlternatives.findIndex((alt) => alt.idAlternative == idAlternative);
			alternative = question.dropdownAlternatives[alternativeIndex];
			Vue.set(
				alternative,
				"conditionGroup",
				mapConditionGroupFromBackNEW(question.idQuestion, idAlternative, conditionGroup)
			);
			break;

		case Vue.prototype.$ResponseType.Concatenation:
			alternativeIndex = question.concatenationAlternatives.findIndex((alt) => alt.idAlternative == idAlternative);
			alternative = question.concatenationAlternatives[alternativeIndex];
			Vue.set(
				alternative,
				"conditionGroup",
				mapConditionGroupFromBackNEW(question.idQuestion, idAlternative, conditionGroup)
			);
			break;
	}
};

const updateActualRequirementQuestions = (loadedQuestions, actualRequirement) => {
	loadedQuestions?.forEach((loadedQuestion) => {
		const matchingQuestionIndex = findQuestionIndexById(actualRequirement.questions, loadedQuestion.idQuestion);

		if (matchingQuestionIndex !== -1) {
			Object.assign(actualRequirement.questions[matchingQuestionIndex], loadedQuestion);
		} else {
			actualRequirement.questions.push(loadedQuestion);
		}
	});
};

const removeUnmatchedQuestions = (actualRequirement, requirementDetails) => {
	if (!actualRequirement && !requirementDetails) return;

	const actualQuestionIds = actualRequirement.questions?.map((question) => question.idQuestion);
	const detailQuestionIds = requirementDetails.questions?.map((question) => question.idQuestion);
	const idsToRemove = actualQuestionIds?.filter((id) => !detailQuestionIds.includes(id));

	idsToRemove?.forEach((id) => {
		const questionIndex = findQuestionIndexById(actualRequirement.questions, id);
		actualRequirement.questions?.splice(questionIndex, 1);
	});
};

const findQuestionIndexById = (questions, id) => {
	return questions?.findIndex((question) => question.idQuestion === id);
};

const updateRequirementQuestionDetails = (state, stateQuestion) => {
	const existingQuestionIndex = state.requirementQuestionDetails.findIndex(
		(question) => question.idQuestion === stateQuestion.idQuestion
	);

	if (existingQuestionIndex === -1) {
		state.requirementQuestionDetails.push(stateQuestion);
	} else {
		Vue.set(state.requirementQuestionDetails, existingQuestionIndex, stateQuestion);
	}
};
